import { useEffect, useRef } from 'react';
import { useSessionStorage } from '@minecraft.utils';
import { useRouter } from './RouteProvider';

type UseClearSessionStorageDependingOnRouteArgs = {
  /** Session-storage keys to remove when the pathname does not match. */
  sessionStorageKeys: string[];
  /** Pathname patterns where the session-storage values should be preserved. Use `:param` for a dynamic segment. */
  matchPathValues: string[];
};

const pathMatchesPattern = (pathname: string, pattern: string): boolean => {
  const patternParts = pattern.split('/');
  const pathParts = pathname.split('/');

  if (patternParts.length !== pathParts.length) return false;

  return patternParts.every((part, i) => part.startsWith(':') || part === pathParts[i]);
};

const shouldSessionStorageBeCleared = ({
  newLocation,
  matchPathValues,
}: {
  newLocation: string;
  matchPathValues: string[];
}): boolean => {
  if (matchPathValues.length > 0) {
    return !matchPathValues.some((pattern) => pathMatchesPattern(newLocation, pattern));
  }
  return false;
};

/**
 * Clears selected session-storage values when navigation leaves a set of paths.
 *
 * The hook skips the initial render and evaluates the current pathname after
 * each subsequent route change. A pathname is considered a match when it has
 * the same number of segments as a pattern and each segment is equal, unless
 * the pattern segment starts with `:`. Values are cleared only when none of
 * the supplied patterns match. An empty `matchPathValues` array never clears
 * storage.
 *
 * Use this hook in a component that is rendered within `RouteProvider`:
 *
 * ```tsx
 * import { useClearSessionStorageDependingOnRoute } from '@blocs.router';
 *
 * useClearSessionStorageDependingOnRoute({
 *   sessionStorageKeys: ['FavoriteListsKey'],
 *   matchPathValues: ['/favorites-lists/:listId', '/talent'],
 * });
 * ```
 *
 * It is currently used by `FavoritesListView` and `FavoriteListsWidget` in
 * `packages/enterprise-casting` to preserve favorite-list state on the
 * favorite-list and talent routes and clear it when leaving those routes.
 */
export const useClearSessionStorageDependingOnRoute = ({
  sessionStorageKeys,
  matchPathValues,
}: UseClearSessionStorageDependingOnRouteArgs): void => {
  const { pathname } = useRouter();
  const sessionStorage = useSessionStorage({});

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const clearSessionStorage = shouldSessionStorageBeCleared({
      newLocation: pathname,
      matchPathValues,
    });

    if (clearSessionStorage) {
      sessionStorageKeys.forEach((key) => {
        sessionStorage.clear(key);
      });
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
};
